import { useState } from 'react';
import type { Fixture } from '@h2h/shared';
import { ChevronDown } from '../../components/ui/icons';
import { LivePill, MarketDots, MultiplierBadge } from '../../components/rounds/shared';
import type { PlayerPickReveal } from '../../lib/api';
import { signedPoints } from '../../lib/format';
import type { CardVM } from '../../lib/round';
import { cn } from '../../lib/cn';
import { fixtureMarketRows } from './helpers';
import { MarketLine } from './MarketLine';

/**
 * One revealed football pick. Reads like the rest of the app at a glance — confidence
 * weight, the matchup (with the live score picked out), the market dots + a one-line
 * verdict, and the points it banked or projects. Tap to expand the full per-market
 * breakdown of exactly what they backed: progressive disclosure keeps the history compact
 * while still letting you scout a rival pick by pick.
 */
export function ProfileFootballPickCard({
  vm,
  pick,
  fixture,
  embedded = false
}: {
  vm: CardVM;
  pick: PlayerPickReveal;
  fixture: Fixture | undefined;
  /** Flat row inside the unified profile history card. */
  embedded?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rows = fixtureMarketRows(pick, fixture);
  const canExpand = rows.length > 0;
  const isLive = vm.status === 'live';
  const isFinal = vm.status === 'final';
  const isVoid = vm.status === 'void';
  // PERFECT MATCH: base hit + ≥1 gradeable boost, all landed. Void/ungradeable
  // boosts stay 'open' and are excluded, so this agrees with the engine grader.
  const gradeableLegs = vm.legs.filter((l) => l.state !== 'open').length;
  const landedLegs = vm.legs.filter((l) => l.state === 'hit').length;
  const perfect = vm.anchorState === 'hit' && gradeableLegs > 0 && landedLegs === gradeableLegs;

  return (
    <div
      className={cn(
        embedded ? 'bg-white' : 'overflow-hidden rounded-[16px] border bg-fill/60',
        !embedded && (isLive ? 'border-[1.5px] border-accent/45' : 'border-black/[0.06]')
      )}
    >
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center gap-3 text-left',
          embedded ? 'px-[18px] py-3.5' : 'p-[14px_15px]',
          canExpand && 'tap active:bg-wash'
        )}
      >
        <MultiplierBadge multiplier={vm.multiplier} active={isLive} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[7px]">
            <span className="num text-[14px] font-extrabold tracking-snug text-ink">
              {isLive && vm.liveScore ? (
                <>
                  {vm.homeCode} <span className="text-accent-ink">{vm.liveScore}</span> {vm.awayCode}
                </>
              ) : (
                vm.fixtureLabel
              )}
            </span>
            {isLive ? (
              <span className="inline-flex items-center gap-[5px]">
                <LivePill />
                {vm.liveClock && <span className="num text-[10px] font-bold text-accent-ink">{vm.liveClock}</span>}
              </span>
            ) : isFinal ? (
              <span className="text-[10px] font-bold tracking-wide text-ink3">FT</span>
            ) : isVoid ? (
              <span className="text-[10px] font-bold tracking-wide text-ink3">VOID</span>
            ) : null}
          </div>
          <div className="mt-[7px] flex items-center gap-[6px]">
            <MarketDots markets={vm.markets} />
            <span
              className={cn(
                'truncate text-[10.5px] font-semibold',
                isVoid ? 'text-ink3' : perfect || isLive ? 'text-accent-ink' : 'text-ink2'
              )}
            >
              {vm.marketSummary}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-[10px]">
          {isFinal && vm.points != null ? (
            <div className="text-right">
              <div
                className={cn(
                  'num text-[18px] font-extrabold leading-none',
                  vm.points > 0 ? 'text-accent-ink' : 'text-ink'
                )}
              >
                {signedPoints(vm.points)}
              </div>
              {vm.formula && vm.points != null && vm.points > 0 && (
                <div className="num mt-[3px] text-[10px] font-semibold text-ink3">{vm.formula}</div>
              )}
            </div>
          ) : isLive && vm.livePoints != null ? (
            <div className="text-right">
              <div className="num text-[18px] font-extrabold leading-none text-accent-ink">
                {signedPoints(vm.livePoints)}
              </div>
              <div className="mt-[3px] text-[10px] font-semibold text-ink3">projected</div>
            </div>
          ) : vm.points == null && vm.status !== 'void' ? (
            <div className="text-right">
              <span className="text-[11px] font-bold text-ink3">{vm.anchorState === 'open' ? 'Awaiting kickoff' : 'In play'}</span>
            </div>
          ) : null}
          {canExpand && (
            <ChevronDown
              width={16}
              height={16}
              strokeWidth={2.4}
              className={cn('shrink-0 text-ink4 transition-transform', open && 'rotate-180')}
            />
          )}
        </div>
      </button>

      {open && canExpand && (
        <div className="flex flex-col gap-[11px] border-t border-black/[0.06] px-[15px] pb-[15px] pt-[13px]">
          {rows.map((row) => (
            <MarketLine key={row.label} label={row.label} value={row.value} state={row.state} factor={row.factor} />
          ))}
        </div>
      )}
    </div>
  );
}
